<template>
  <div v-if="tableData" class="table">
    <AgGridWrapper
      ref="ag-grid"
      :options="gridOptions"
      store-namespace="subscriptions"
      :title="`${departmentLabel.singular} spend overview - all ${departmentLabel.plural.toLowerCase()}`"
      no-resize
      has-export-excel
      @hook:mounted="agGridMounted"
      @export-to-excel="exportToExcel"
    >
      <template #header-actions-before>
        <YearSelect v-model="financialPeriodId" :dark="false" />
      </template>

      <template #footerActions>
        <span class="justify-end flex fit q-pr-md">Local currency ({{ settings.currency }})</span>
        <div class="row justify-end flex fit q-pa-md">
          <q-btn :to="`/financial-periods?year=${new Date().getFullYear()}`" color="tertiary" size="sm" class="q-mr-sm">
            Set Financial Period
            <LmTooltip>
              Manage the accounting year for subscription periods.
            </LmTooltip>
          </q-btn>

          <q-btn color="tertiary" size="sm" to="/departments">
            Go to {{ departmentLabel.singular }} page
            <LmTooltip> Add, remove {{ departmentLabel.plural.toLowerCase() }}, update budgets. </LmTooltip>
          </q-btn>
        </div>
      </template>
    </AgGridWrapper>
  </div>
  <q-card v-else class="loading">
    <q-card-section style="height: 560px">
      <SpinnerAudioLoader :loading="true" />
    </q-card-section>
  </q-card>
</template>

<script>
import * as apiTo from '../api'
import { formatPrice } from '@/lib/formatting.utils'
// Libraries
import { cloneDeep, capitalize } from 'lodash'
import { mapState, mapGetters } from 'vuex'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import SpinnerAudioLoader from '@/reusable/base-components/SpinnerAudioLoader'
import YearSelect from '@/modules/financial/shared/YearSelect'

export default {
  components: {
    AgGridWrapper,
    SpinnerAudioLoader,
    YearSelect
  },

  data: () => ({
    tableData: null,
    financialPeriodId: null
  }),

  computed: {
    ...mapState('financial-insights', ['financialPeriods']),
    ...mapGetters('entity-labels', ['departmentLabel']),

    gridOptions() {
      if (!this.tableData) return
      const rowData = this.rows
      const columnDefs = this.columnsDefs
      return {
        rowData,
        columnDefs,
        defaultColDef: {
          sortable: false,
          suppressMenu: true
        },
        rowClassRules: {
          'styled-row': params => [3, 7, 9, 10].includes(params.data.id)
        }
      }
    },

    columns() {
      const cols = this.getColFields()
      return this.tableData.reduce(
        (acc, curr, index) => {
          acc.push({
            name: capitalize(curr.name),
            field: cols[index]
          })
          return acc
        },
        [{ name: 'Renewal & offers', field: 'first_column' }]
      )
    },

    rows() {
      return this.generateAgGridRows()
    },

    columnsDefs() {
      return this.columns.reduce((acc, curr) => {
        acc.push({
          headerName: curr.name,
          field: curr.field,
          width: curr.field === 'first_column' ? 250 : null,
          valueGetter: params => {
            if (curr.field === 'first_column') {
              return params.data[curr.field]
            }
            return formatPrice(params.data[curr.field]) || params.data[curr.field]
          },
          tooltipValueGetter: params => {
            if (curr.field === 'first_column') {
              return params.data[curr.field]
            }
            return formatPrice(params.data[curr.field]) || params.data[curr.field]
          },
          comparator: valueA => valueA
        })
        return acc
      }, [])
    }
  },

  watch: {
    financialPeriodId: {
      handler: 'getTableData'
    },

    financialPeriods: {
      handler: 'setDefaultSelectedPeriod',
      immediate: true
    }
  },

  methods: {
    setDefaultSelectedPeriod(periods) {
      if (!periods || !!this.financialPeriodId) return

      const period = periods.find(p => !!p.is_current)
      this.financialPeriodId = period.id
    },

    getTableData(periodId) {
      apiTo.getTableData(periodId).then(({ data }) => (this.tableData = data.result))
    },

    generateAgGridRows() {
      const tableData = cloneDeep(this.tableData)
      const firstColumn = this.getFirstColumnRows()
      const rows = this.getRows()

      return rows.reduce((acc, row, index) => {
        let obj = { id: index + 1 }
        tableData.forEach(dep => {
          obj.first_column = firstColumn[index]
          obj[dep.name] = dep.spend_data[row]
        })
        acc.push(obj)
        return acc
      }, [])
    },

    getRows() {
      return [
        'accepted_renewals',
        'accepted_new_offers',
        'current_spend',
        'pending_renewals',
        'pending_forecasted_renewals',
        'pending_unpriced_renewals_using_last_years_price',
        'total_expected_spend',
        'pending_new_offers',
        'total_expected_spend_including_new_offers',
        'budget'
      ]
    },

    getFirstColumnRows() {
      return [
        'Accepted Renewals',
        'Accepted New Offers',
        'Current Spend',
        'Pending Renewals w/prices',
        'Pending Renewals w/forecasted prices',
        "Pending Renewals using last year's prices",
        'Total Expected Spend',
        'Pending New Offers',
        'Total Expected Spend incl. New Offers',
        'Budget'
      ]
    },

    getColFields() {
      return this.tableData.reduce((acc, curr) => {
        acc.push(curr.name)
        return acc
      }, [])
    },

    agGridMounted() {
      this.$refs['ag-grid'].setColumnPinned('first_column', 'left')
    },

    exportToExcel(options) {
      options.api.exportDataAsExcel({
        fileName: 'financial-insights',
        sheetName: 'financial-insights'
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
>>> .styled-row {
  font-weight bold
}
</style>
